import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	CircularProgress,
	Select,
	InputLabel,
	MenuItem,
	FormControl,
	FormControlLabel,
	Grid,
	Checkbox,
	TextField,
	Typography,
	InputAdornment,
	IconButton
  } from '@material-ui/core';

import { Visibility, VisibilityOff } from '@material-ui/icons';

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useWhatsApps from "../../hooks/useWhatsApps";
import { profilePermissionGroups } from "../../utils/profilePermissions";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
	permissionsGrid: {
		marginTop: theme.spacing(1),
	},
	permissionGroup: {
		borderTop: "1px solid rgba(0, 0, 0, 0.08)",
		paddingTop: theme.spacing(0.5),
		marginTop: theme.spacing(0.5),
	},
	permissionGroupTitle: {
		fontWeight: 600,
		marginBottom: theme.spacing(0.25),
	},
	permissionGroupActions: {
		paddingLeft: theme.spacing(1.5),
	},
	permissionItem: {
		minHeight: 28,
		marginTop: -4,
		marginBottom: -4,
		"& .MuiFormControlLabel-label": {
			fontSize: "0.875rem",
		},
	},
}));

const UserSchema = Yup.object().shape({
name: Yup.string()
  .min(2, "Muito curto!")
  .max(50, "Muito longo!")
  .required("Obrigatório"),
	password: Yup.string().min(5, "Muito curta!").max(50, "Muito longa!"),
	email: Yup.string().email("E-mail inválido").required("Obrigatório"),
});

const UserModal = ({ open, onClose, userId }) => {
	const classes = useStyles();

	const initialState = {
		name: "",
		email: "",
		password: "",
		profile: "user",
		extraPermissions: []
	};

	const { user: loggedInUser } = useContext(AuthContext);

	const [user, setUser] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [showPassword, setShowPassword] = useState(false);
	const [whatsappId, setWhatsappId] = useState(false);
	const [profiles, setProfiles] = useState([]);
	const {loading, whatsApps} = useWhatsApps();

	const canEditUserProfile =
		loggedInUser?.profile === "admin" ||
		loggedInUser?.permissions?.includes("user-modal:editProfile");

	useEffect(() => {
		const fetchProfiles = async () => {
			try {
				const { data } = await api.get("/profiles");
				setProfiles(data);
			} catch (err) {
				toastError(err);
			}
		};

		if (open && canEditUserProfile) {
			fetchProfiles();
		}
	}, [open, canEditUserProfile]);

	useEffect(() => {
		const fetchUser = async () => {
			if (!userId) return;
			try {
				const { data } = await api.get(`/users/${userId}`);
				setUser(prevState => {
					return { ...prevState, ...data };
				});
				const userQueueIds = data.queues?.map(queue => queue.id);
				setSelectedQueueIds(userQueueIds);
				setWhatsappId(data.whatsappId ? data.whatsappId : '');
			} catch (err) {
				toastError(err);
			}
		};

		fetchUser();
	}, [userId, open]);

	const handleClose = () => {
		onClose();
		setUser(initialState);
		setProfiles([]);
	};

	const handleSaveUser = async values => {
		const inheritedPermissions =
			profiles.find(profile => profile.key === values.profile)?.permissions || [];
		const extraPermissions = (values.extraPermissions || []).filter(
			permission => {
				const inheritedByExactPermission = inheritedPermissions.includes(permission);
				const inheritedByRootPermission = profilePermissionGroups.some(group =>
					group.root &&
					inheritedPermissions.includes(group.root.key) &&
					group.permissions.some(item => item.key === permission)
				);

				return !inheritedByExactPermission && !inheritedByRootPermission;
			}
		);
		const userData = {
			...values,
			extraPermissions,
			whatsappId,
			queueIds: selectedQueueIds
		};
		try {
			if (userId) {
				await api.put(`/users/${userId}`, userData);
			} else {
				await api.post("/users", userData);
			}
			toast.success(i18n.t("userModal.success"));
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="md"
				fullWidth
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{userId
						? `${i18n.t("userModal.title.edit")}`
						: `${i18n.t("userModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={UserSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveUser(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => {
						const inheritedPermissions =
							profiles.find(profile => profile.key === values.profile)?.permissions || [];
						const extraPermissions = values.extraPermissions || [];
						const toggleExtraPermission = permission => {
							const nextPermissions = extraPermissions.includes(permission)
								? extraPermissions.filter(item => item !== permission)
								: [...extraPermissions, permission];

							setFieldValue("extraPermissions", nextPermissions);
						};

						return (
							<Form>
							<DialogContent dividers>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("userModal.form.name")}
										autoFocus
										name="name"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
									<Field
										as={TextField}
										name="password"
										variant="outlined"
										margin="dense"
										label={i18n.t("userModal.form.password")}
										error={touched.password && Boolean(errors.password)}
										helperText={touched.password && errors.password}
										type={showPassword ? 'text' : 'password'}
										InputProps={{
										endAdornment: (
											<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={() => setShowPassword((e) => !e)}
											>
												{showPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
											</InputAdornment>
										)
										}}
										fullWidth
									/>
								</div>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("userModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
									<FormControl
										variant="outlined"
										className={classes.formControl}
										margin="dense"
									>
										<Can
											role={loggedInUser.profile}
											perform="user-modal:editProfile"
											yes={() => (
												<>
													<InputLabel id="profile-selection-input-label">
														{i18n.t("userModal.form.profile")}
													</InputLabel>

													<Field
														as={Select}
														label={i18n.t("userModal.form.profile")}
														name="profile"
														labelId="profile-selection-label"
														id="profile-selection"
														required
													>
														{profiles.map(profile => (
															<MenuItem key={profile.key} value={profile.key}>
																{profile.name}
															</MenuItem>
														))}
													</Field>
												</>
											)}
										/>
									</FormControl>
								</div>
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editQueues"
									yes={() => (
										<QueueSelect
											selectedQueueIds={selectedQueueIds}
											onChange={values => setSelectedQueueIds(values)}
										/>
									)}
								/>
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editQueues"
									yes={() => (!loading &&
										<FormControl variant="outlined" margin="dense" className={classes.maxWidth} fullWidth>
											<InputLabel>{i18n.t("userModal.form.whatsapp")}</InputLabel>
											<Field
												as={Select}
												value={whatsappId}
												onChange={(e) => setWhatsappId(e.target.value)}
												label={i18n.t("userModal.form.whatsapp")}
											>
												<MenuItem value={''}>&nbsp;</MenuItem>
												{whatsApps.map((whatsapp) => (
													<MenuItem key={whatsapp.id} value={whatsapp.id}>{whatsapp.name}</MenuItem>
												))}
											</Field>
										</FormControl>
									)}
								/>
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editProfile"
									yes={() => (
										<>
											<Typography variant="subtitle2" color="textSecondary">
												Permissões extras por funcionalidade
											</Typography>
											<div className={classes.permissionsGrid}>
												{profilePermissionGroups.map(group => (
													<div className={classes.permissionGroup} key={group.label}>
														<Typography className={classes.permissionGroupTitle} variant="body2">
															{group.label}
														</Typography>
														{group.root && (
															<FormControlLabel
																className={classes.permissionItem}
																control={
																	<Checkbox
																		color="primary"
																		size="small"
																		disabled={inheritedPermissions.includes(group.root.key)}
																		checked={
																			inheritedPermissions.includes(group.root.key) ||
																			extraPermissions.includes(group.root.key)
																		}
																		onChange={() => toggleExtraPermission(group.root.key)}
																	/>
																}
																label={
																	inheritedPermissions.includes(group.root.key)
																		? `${group.root.label} (perfil)`
																		: group.root.label
																}
															/>
														)}
														<Grid container spacing={1} className={classes.permissionGroupActions}>
															{group.permissions.map(permission => {
																const inherited =
																	inheritedPermissions.includes(permission.key) ||
																	(group.root &&
																		inheritedPermissions.includes(group.root.key));

																return (
																	<Grid item xs={12} sm={6} key={permission.key}>
																		<FormControlLabel
																			className={classes.permissionItem}
																			control={
																				<Checkbox
																					color="primary"
																					size="small"
																					disabled={inherited}
																					checked={
																						inherited ||
																						extraPermissions.includes(permission.key)
																					}
																					onChange={() =>
																						toggleExtraPermission(permission.key)
																					}
																				/>
																			}
																			label={
																				inherited
																					? `${permission.label} (perfil)`
																					: permission.label
																			}
																		/>
																	</Grid>
																);
															})}
														</Grid>
													</div>
												))}
											</div>
										</>
									)}
								/>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("userModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{userId
										? `${i18n.t("userModal.buttons.okEdit")}`
										: `${i18n.t("userModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
						);
					}}
				</Formik>
			</Dialog>
		</div>
	);
};

export default UserModal;
